import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PrefetchLink from '@/components/shared/PrefetchLink';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import GlobalSearch from '@/components/search/GlobalSearch';
import NavDropdown, { type NavDropdownGroup } from '@/components/layout/NavDropdown';

// Logo is served from public folder for optimal LCP - preloaded in index.html
const yoRanchLogo = '/images/yo-ranch-logo.webp';

export const aboutLinks: NavDropdownGroup[] = [
  {
    group: 'The Family & Ranch',
    items: [
      { name: 'Y.O. Ranch Heritage Overview', href: '/yo-ranch-heritage' },
      { name: 'Schreiner Dynasty', href: '/schreiner-dynasty' },
      { name: 'Schreiner Family Tree', href: '/schreiner-family-tree' },
      { name: 'Captain Schreiner', href: '/captain-schreiner' },
      { name: 'Charles Schreiner III', href: '/schreiner-iii' },
      { name: 'Ranch History', href: '/ranch-history' },
      { name: 'Schreiner Family Album', href: '/schreiner-family-album' },
      { name: 'Vital Records', href: '/vital-records' },
      { name: 'Saddle Room Archive', href: '/saddle-room-archive' },
    ],
  },
  {
    group: 'Collections & Stewardship',
    items: [
      { name: 'The Museum', href: '/museum' },
      { name: 'On Display Now', href: '/explore?view=stories' },
      { name: 'Culture & Fame', href: '/culture-and-fame' },
      { name: 'Hunting Heritage', href: '/hunting-heritage' },
      { name: 'Stewardship', href: '/stewardship' },
      { name: 'Land History Timeline', href: '/land-history' },
      { name: 'Tract Archive by County', href: '/land-history/archive' },
      { name: 'Deeds Archive by County', href: '/land-history/deeds' },
      { name: 'YO Historic Land Documents', href: '/yo-historic-land-documents' },
    ],
  },
];

export const topicsLinks: NavDropdownGroup[] = [
  {
    group: 'Topics',
    items: [
      { name: 'Texas Rangers', href: '/texas-rangers' },
      { name: 'Texas Longhorns', href: '/texas-longhorn-heritage' },
      { name: 'Exotic Wildlife Heritage', href: '/wildlife-heritage' },
      { name: 'First Peoples of the Y.O.', href: '/native-american-heritage' },
      { name: 'Famous Visitors', href: '/famous-visitors' },
    ],
  },
];

const simpleNav = [
  { name: 'Explore', href: '/explore' },
  { name: 'Documents', href: '/document-search' },
  { name: 'Visit', href: '/visit' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    // Non-home pages always render the solid header — no scroll listener needed.
    if (!isHomePage) {
      setIsScrolled(true);
      return;
    }

    // On home, flip to solid once the user scrolls past the hero (minus header height).
    const getThreshold = () => {
      const hero = document.querySelector<HTMLElement>('[data-hero-section]');
      const headerEl = document.querySelector<HTMLElement>('header');
      const headerH = headerEl?.offsetHeight ?? 96;
      const heroH = hero?.offsetHeight ?? window.innerHeight;
      return Math.max(50, heroH - headerH);
    };

    let threshold = getThreshold();
    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > threshold);
        rafId = null;
      });
    };

    const handleResize = () => {
      threshold = getThreshold();
      handleScroll();
    };

    // Run once to sync initial state (e.g., navigating back to home mid-scroll).
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isHomePage]);

  const isTransparent = isHomePage && !isScrolled;
  const headerBg = isTransparent
    ? 'bg-transparent'
    : 'bg-background/95 backdrop-blur-sm shadow-sm border-b border-border/50';

  const isActive = (href: string) => location.pathname === href;
  const groupActive = (groups: NavDropdownGroup[]) =>
    groups.some((g) => g.items.some((i) => isActive(i.href)));

  const dropdownTriggerClasses = (groups: NavDropdownGroup[]) =>
    cn(
      'relative flex items-center gap-1 px-3 py-2 font-sans text-xs tracking-widest uppercase transition-all duration-300',
      'after:absolute after:bottom-1 after:left-3 after:right-3 after:h-px after:origin-left',
      'after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100',
      'focus:outline-none focus-visible:ring-2 rounded-sm',
      isTransparent
        ? 'after:bg-hero-text focus-visible:ring-hero-text/40'
        : 'after:bg-foreground focus-visible:ring-foreground/40',
      groupActive(groups)
        ? (isTransparent ? 'text-hero-text' : 'text-foreground') + ' after:scale-x-100'
        : (isTransparent ? 'text-hero-text/70 hover:text-hero-text' : 'text-muted-foreground hover:text-foreground'),
    );

  const simpleLinkClasses = (href: string) =>
    cn(
      "relative px-3 py-2 font-sans text-xs tracking-widest uppercase transition-colors",
      "after:absolute after:bottom-1 after:left-3 after:right-3 after:h-px after:origin-left",
      "after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100",
      isTransparent ? "after:bg-hero-text" : "after:bg-foreground",
      isActive(href)
        ? (isTransparent ? "text-hero-text" : "text-foreground") + " after:scale-x-100"
        : (isTransparent ? "text-hero-text/70 hover:text-hero-text" : "text-muted-foreground hover:text-foreground"),
    );


  return (
    <header data-site-header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      headerBg,
    )}>
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <PrefetchLink to="/" className="flex-shrink-0 group" aria-label="Y.O. Ranch Headquarters — Home">
            <span
              role="img"
              aria-label="Y.O. Ranch Headquarters"
              className="block h-14 md:h-16 transition-all duration-300"
              style={{
                aspectRatio: '45 / 56',
                backgroundColor: isTransparent
                  ? 'hsl(var(--warm-brown))'
                  : 'hsl(var(--foreground))',
                WebkitMaskImage: `url(${yoRanchLogo})`,
                maskImage: `url(${yoRanchLogo})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
              }}
            />
          </PrefetchLink>

          {/* Desktop Navigation - exactly 5 items */}
          <div className="hidden lg:flex lg:items-center lg:gap-0.5">
            <NavDropdown
              label="Y.O. Ranch Heritage"
              groups={aboutLinks}
              triggerClassName={dropdownTriggerClasses(aboutLinks)}
              active={groupActive(aboutLinks)}
              isItemActive={isActive}
              lightMode={!isTransparent}
            />

            <PrefetchLink to="/explore" className={simpleLinkClasses('/explore')}>
              Explore
            </PrefetchLink>

            <PrefetchLink
              to="/explore?view=stories"
              className={simpleLinkClasses('/explore?view=stories')}
            >
              Exhibits
            </PrefetchLink>

            <PrefetchLink to="/on-display" className={simpleLinkClasses('/on-display')}>
              On Display
            </PrefetchLink>

            <NavDropdown
              label="Topics"
              groups={topicsLinks}
              triggerClassName={dropdownTriggerClasses(topicsLinks)}
              active={groupActive(topicsLinks)}
              isItemActive={isActive}
              lightMode={!isTransparent}
            />

            <PrefetchLink to="/document-search" className={simpleLinkClasses('/document-search')}>
              Documents
            </PrefetchLink>

            <PrefetchLink to="/visit" className={simpleLinkClasses('/visit')}>
              Visit
            </PrefetchLink>

            {/* Global Search */}
            <GlobalSearch lightMode={!isTransparent} />
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              className={cn(
                isTransparent
                  ? "text-hero-text hover:bg-hero-text/10"
                  : "text-foreground hover:bg-foreground/10",
              )}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation - mirrors 5-item structure */}
        {mobileMenuOpen && (
          <div
            id="mobile-navigation"
            className={cn(
              "lg:hidden fixed inset-x-0 top-20 h-[calc(100dvh-5rem)] overflow-y-auto z-40 border-t",
              isTransparent
                ? "bg-hero border-hero-text/10"
                : "bg-background border-border",
            )}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="py-4">
              <Accordion type="multiple" className="w-full">
                {/* About */}
                <AccordionItem value="about" className={isTransparent ? "border-hero-text/10" : "border-border"}>
                  <AccordionTrigger className={cn(
                    "px-4 py-3 font-sans text-sm tracking-widest uppercase hover:no-underline",
                    isTransparent
                      ? "text-hero-text/70 hover:text-hero-text"
                      : "text-muted-foreground hover:text-foreground",
                  )}>
                    Y.O. Ranch Heritage
                  </AccordionTrigger>
                  <AccordionContent className={cn("pb-2", isTransparent ? "bg-hero" : "bg-background")}>
                    {aboutLinks.map((group) => (
                      <div key={group.group}>
                        <div className={cn(
                          "px-4 py-2 font-sans text-[10px] tracking-widest uppercase",
                          isTransparent ? "text-hero-text/40" : "text-muted-foreground",
                        )}>
                          {group.group}
                        </div>
                        {group.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              "block px-6 py-2.5 font-sans text-sm tracking-wide transition-colors",
                              isActive(item.href)
                                ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                                : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                            )}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>

                {/* Explore */}
                <div className={isTransparent ? "border-b border-hero-text/10" : "border-b border-border"}>
                  <Link
                    to="/explore"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block px-4 py-4 font-sans text-sm tracking-widest uppercase transition-colors",
                      isActive('/explore')
                        ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                        : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                    )}
                  >
                    Explore
                  </Link>
                </div>

                {/* Exhibits — funnel to the Explore types (Collections) view */}
                <div className={isTransparent ? "border-b border-hero-text/10" : "border-b border-border"}>
                  <Link
                    to="/explore?view=stories"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block px-4 py-4 font-sans text-sm tracking-widest uppercase transition-colors",
                      isActive('/explore?view=stories')
                        ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                        : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                    )}
                  >
                    Exhibits
                  </Link>
                </div>

                {/* On Display — comprehensive index of every on-view artifact and exhibit */}
                <div className={isTransparent ? "border-b border-hero-text/10" : "border-b border-border"}>
                  <Link
                    to="/on-display"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block px-4 py-4 font-sans text-sm tracking-widest uppercase transition-colors",
                      isActive('/on-display')
                        ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                        : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                    )}
                  >
                    On Display
                  </Link>
                </div>

                {/* Topics */}
                <AccordionItem value="topics" className={isTransparent ? "border-hero-text/10" : "border-border"}>
                  <AccordionTrigger className={cn(
                    "px-4 py-3 font-sans text-sm tracking-widest uppercase hover:no-underline",
                    isTransparent
                      ? "text-hero-text/70 hover:text-hero-text"
                      : "text-muted-foreground hover:text-foreground",
                  )}>
                    Topics
                  </AccordionTrigger>
                  <AccordionContent className={cn("pb-2", isTransparent ? "bg-hero" : "bg-background")}>
                    {topicsLinks.map((group) => (
                      <div key={group.group}>
                        {group.items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={cn(
                              "block px-6 py-2.5 font-sans text-sm tracking-wide transition-colors",
                              isActive(item.href)
                                ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                                : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                            )}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>

                {/* Documents */}
                <div className={isTransparent ? "border-b border-hero-text/10" : "border-b border-border"}>
                  <Link
                    to="/document-search"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block px-4 py-4 font-sans text-sm tracking-widest uppercase transition-colors",
                      isActive('/document-search')
                        ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                        : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                    )}
                  >
                    Documents
                  </Link>
                </div>

                {/* Visit */}
                <div className={isTransparent ? "border-b border-hero-text/10" : "border-b border-border"}>
                  <Link
                    to="/visit"
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "block px-4 py-4 font-sans text-sm tracking-widest uppercase transition-colors",
                      isActive('/visit')
                        ? (isTransparent ? "text-hero-text bg-hero-text/5" : "text-foreground bg-secondary/50")
                        : (isTransparent ? "text-hero-text/70 hover:text-hero-text hover:bg-hero-text/5" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"),
                    )}
                  >
                    Visit
                  </Link>
                </div>
              </Accordion>

              {/* Global Search for Mobile */}
              <div className={cn(
                "mt-4 pt-4 px-4 border-t",
                isTransparent ? "border-hero-text/10" : "border-border",
              )}>
                <GlobalSearch />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
